import { useState, useEffect } from "react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Network, Eye, EyeOff, BarChart3 } from "lucide-react"
import Tree from "../features/simulation-decisions/components/tree"
import { TreeItemNode } from "../features/simulation-decisions/types"
import { 
  convertActionsToTreeNodes, 
  getActionsSummary, 
  type ActionCategory 
} from "../utils/actionsToTreeConverter"

type ActionsTreeDisplayProps = {
  actions: ActionCategory[]
  title?: string
  description?: string
}

export const ActionsTreeDisplay = ({ 
  actions, 
  title = "Generated Actions",
  description = "Explore the decision tree for this simulation"
}: ActionsTreeDisplayProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [treeNodes, setTreeNodes] = useState<TreeItemNode[]>([])
  const [summary, setSummary] = useState<ReturnType<typeof getActionsSummary> | null>(null)

  // Initialize tree nodes and summary when actions change
  useEffect(() => {
    if (actions && actions.length > 0) {
      const nodes = convertActionsToTreeNodes(actions)
      setTreeNodes(nodes)
      setSummary(getActionsSummary(actions))
    }
  }, [actions])

  const expandAll = (nodes: TreeItemNode[] = treeNodes) => {
    nodes.forEach(node => {
      node.isExpanded = true
      if (node.children) {
        expandAll(node.children)
      }
    })
    setTreeNodes([...treeNodes]) // Force re-render
  }

  const collapseAll = (nodes: TreeItemNode[] = treeNodes) => {
    nodes.forEach(node => {
      node.isExpanded = false
      if (node.children) {
        collapseAll(node.children)
      }
    })
    setTreeNodes([...treeNodes]) // Force re-render
  }

  if (!actions || actions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No actions generated yet. Generate actions to see the decision tree.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              {title}
              {summary && (
                <Badge variant="secondary">
                  {summary.leafActions} actions
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {isExpanded ? "Hide" : "Show"}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          {/* Summary Stats */}
          {summary && (
            <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-sm">
                  {summary.totalCategories} categories, {summary.leafActions} final actions
                </span>
              </div>
            </div>
          )}

          {/* Tree Controls */}
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => expandAll()}
            >
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => collapseAll()}
            >
              Collapse All
            </Button>
          </div>

          {/* Actions Tree */}
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            <Tree choices={treeNodes} />
          </div>
        </CardContent>
      )}
    </Card>
  )
}

export default ActionsTreeDisplay